import React, { ChangeEvent, useRef } from 'react';
import styles from './styles.scss';
import connect from 'components/Shared/connect';
import { isMobileLayout, isMobile } from 'components/Shared/responsive';
import Overlay from 'components/Shared/Overlay';
import {
  searchAutocompleteFetchData,
  searchOpen,
  searchClose,
  SEARCH_MINIMUM_LENGTH
} from 'components/Search/actions';
import { AutoCompleteModel } from 'models/AutoCompleteModel';
import { SearchDropdown } from './searchResult';
import { getSearchMatrix, calculateNextItemIndex } from './navigation';
import { useSearch } from './useSearch';
import { getPage } from 'components/Pagelayout/actions';
import { createHref } from 'components/Shared/server';
import { isConcreteSearchItem, isSearchPhrase } from './guards';
import { ISearchItem } from 'models/ISearchItem';
import { selectSearchItem } from 'components/Search/reducer';
import { SearchForm } from 'components/Search/themes/BGH/searchForm';

interface OwnProps {
  className?: string;
}

type MergedProps = OwnProps &
  AutoCompleteModel & {
    isMobileLayout: boolean;
    useMultiColumnlayout: boolean;
    searchFetch: (searchPhrase: string) => Promise<void>;
    open: () => void;
    close: () => void;
    selectitem: (id?: string) => void;
    executeSearch: (url: string) => void;
  };

const NAVIGATION_KEYS: { [key: string]: 'up' | 'down' | 'left' | 'right' } = {
  ArrowDown: 'down',
  ArrowUp: 'up',
  ArrowLeft: 'left',
  ArrowRight: 'right'
};

const getSearchUrl = ({
  searchItem,
  searchPhrase,
  searchUrl
}: {
  searchItem?: ISearchItem;
  searchPhrase: string;
  searchUrl: string;
}) => {
  if (isConcreteSearchItem(searchItem)) {
    return searchItem.url;
  }

  const phrase = isSearchPhrase(searchItem) ? searchItem.phrase : searchPhrase;
  return `${searchUrl}?phrase=${encodeURIComponent(phrase)}`;
};

const COL_COUNT_2 = 2;

const _Search = ({
  suggestion,
  isMobileLayout,
  selectitem,
  useMultiColumnlayout,
  searchUrl,
  executeSearch,
  close,
  searchFetch,
  loadingStatus,
  open,
  showResult: isOpen
}: MergedProps) => {
  const {
    keypress,
    searchPhrase,
    navigateSearchResult,
    currentCol,
    currentRow,
    showResultView,
    reset: _reset
  } = useSearch({
    searchFetchAction: searchFetch,
    loadingStatus
  });
  const searchMatrix = getSearchMatrix({
    suggestion,
    useMultiColumnlayout,
    showResultView
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const TO_SEARCH_HELP_LENGTH = 2;
    const resetsearchHelpSelection =
      searchPhrase.length === SEARCH_MINIMUM_LENGTH &&
      e.target.value.length === TO_SEARCH_HELP_LENGTH;
    if (resetsearchHelpSelection) {
      selectitem(undefined);
    }
    keypress(e.target.value);
  };
  const onSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      close();
      return;
    }

    if (e.key === 'Escape') {
      reset();
      return;
    }
    if (!Object.keys(NAVIGATION_KEYS).includes(e.key)) {
      return;
    }

    const { nextCol, nextRow } = calculateNextItemIndex({
      matrix: searchMatrix,
      currentCol,
      currentRow,
      direction: NAVIGATION_KEYS[e.key]
    });

    const nextItem = searchMatrix[nextCol][nextRow];

    if (nextItem !== undefined) {
      selectitem(nextItem.id);
      navigateSearchResult({ nextCol, nextRow });
    }
  };

  const reset = () => {
    close();
    _reset();
    inputRef.current && inputRef.current.blur();
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const searchItem = searchMatrix[currentCol][currentRow];
    const url = getSearchUrl({ searchItem, searchPhrase, searchUrl });

    if (!window.isReact) {
      location.href = url;
      return;
    }

    executeSearch(url);
    reset();
  };

  return (
    <div className={styles.base}>
      <Overlay lockScroll={isMobileLayout} onClick={close} visible={isOpen}>
        <SearchForm
          onSubmit={onSubmit}
          isOpen={isOpen}
          hasResults={isOpen && searchMatrix.length === COL_COUNT_2}
          searchPhrase={searchPhrase}
          onSearchChange={onSearchChange}
          onSearchKeyDown={onSearchKeyDown}
          open={open}
          inputRef={inputRef}
          reset={reset}
        />
        {isOpen && (
          <SearchDropdown
            {...{
              suggestion,
              searchPhrase,
              reset,
              showResultView,
              executeSearch
            }}
          />
        )}
      </Overlay>
    </div>
  );
};

export const Search = connect<MergedProps, OwnProps>(
  state => ({
    isMobileLayout: isMobileLayout(state),
    useMultiColumnlayout: !isMobile(state),
    ...state.search
  }),
  dispatch => ({
    open: () => dispatch(searchOpen()),
    close: () => dispatch(searchClose()),
    searchFetch: (searchPhrase: string) => dispatch(searchAutocompleteFetchData(searchPhrase)),
    selectitem: (id?: string) => dispatch(selectSearchItem(id)),
    executeSearch: (url: string) =>
      dispatch(getPage(createHref(url))).then(() => dispatch(searchAutocompleteFetchData('')))
  })
)(_Search);
